/**
 * LecturasPage — Registro AM/PM del nivel del tanque por embarcación.
 * Móvil-first: piloto entra en la mañana / tarde, elige su bote, toma
 * foto del medidor, IA lee el nivel, confirma y guarda.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Chip, Button, IconButton, Tooltip, CircularProgress,
  Snackbar, Alert, TextField, MenuItem,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import NuevaLecturaDialog from './NuevaLecturaDialog';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  // Superadmin puede cambiar de empresa desde el sidebar (Layout escribe
  // `empresa_activa` en localStorage). Respetar ese valor por encima de
  // `user.company_id` para no quedar fuera de sync con el selector activo.
  const activa = localStorage.getItem('empresa_activa');
  if (activa) return activa;
  try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.company_id || u.empresa_id || '1'; }
  catch { return '1'; }
}
function getAuthHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

const hoyStr = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const MOMENTO = {
  am: { label: 'AM', bg: '#dbeafe', fg: '#1e40af' },
  pm: { label: 'PM', bg: '#fef3c7', fg: '#92400e' },
};

export default function LecturasPage() {
  const empresaId = getEmpresaId();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [embarcaciones, setEmbarcaciones] = useState([]);
  const [filtros, setFiltros] = useState({
    embarcacion_id: '', momento: '',
    fecha_desde: hoyStr(), fecha_hasta: hoyStr(),
  });
  const [nuevaOpen, setNuevaOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ empresa_id: empresaId });
      Object.entries(filtros).forEach(([k, v]) => { if (v) params.set(k, v); });
      const r = await fetch(`${API_BASE}/turismo/combustible/lecturas?${params}`,
        { headers: getAuthHeaders() });
      if (r.ok) setRows(await r.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [empresaId, filtros]);

  const cargarEmbs = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/turismo/combustible/mis-embarcaciones?empresa_id=${empresaId}`,
        { headers: getAuthHeaders() });
      if (r.ok) setEmbarcaciones(await r.json());
    } catch (e) { console.error(e); }
  }, [empresaId]);

  useEffect(() => { cargarEmbs(); }, [cargarEmbs]);
  useEffect(() => { cargar(); }, [cargar]);

  const setF = (k) => (e) => setFiltros(p => ({ ...p, [k]: e.target.value }));

  // Estado de hoy por embarcación: qué falta capturar
  const estadoHoy = embarcaciones.map(e => {
    const hoy = rows.filter(r => r.embarcacion_id === e.id && r.fecha === hoyStr());
    return {
      embarcacion: e,
      am: hoy.find(r => r.momento === 'am'),
      pm: hoy.find(r => r.momento === 'pm'),
    };
  });

  return (
    <Box>
      {/* Barra superior */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>
            Registra el nivel del tanque <strong>2 veces al día</strong> (mañana y tarde) por embarcación.
            La IA lee el medidor desde la foto.
          </Typography>
        </Box>
        <Tooltip title="Refrescar"><IconButton onClick={cargar}><RefreshIcon /></IconButton></Tooltip>
        <Button variant="contained" startIcon={<AddCircleIcon />}
          onClick={() => setNuevaOpen(true)}
          sx={{
            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
            fontWeight: 700, textTransform: 'none',
            boxShadow: '0 4px 12px rgba(14, 165, 233, 0.35)',
          }}
        >
          Nueva lectura
        </Button>
      </Box>

      {/* Estado del día actual por embarcación */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: '#f8fafc' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#0ea5e9', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Estado de hoy · {fmtDate(hoyStr())}
        </Typography>
        {estadoHoy.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
            No tienes embarcaciones asignadas.
          </Typography>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
            {estadoHoy.map(({ embarcacion, am, pm }) => (
              <Paper key={embarcacion.id} sx={{ p: 1.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', mb: 1 }}>
                  🚤 {embarcacion.nombre}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    icon={am ? <LocalGasStationIcon /> : <CameraAltIcon />}
                    label={am ? `AM: ${am.nivel_galones ?? '—'} gal` : 'AM pendiente'}
                    size="small"
                    sx={{
                      bgcolor: am ? '#dcfce7' : '#fef3c7',
                      color: am ? '#166534' : '#92400e',
                      fontWeight: 700, fontSize: 11,
                    }}
                  />
                  <Chip
                    icon={pm ? <LocalGasStationIcon /> : <CameraAltIcon />}
                    label={pm ? `PM: ${pm.nivel_galones ?? '—'} gal` : 'PM pendiente'}
                    size="small"
                    sx={{
                      bgcolor: pm ? '#dcfce7' : '#fef3c7',
                      color: pm ? '#166534' : '#92400e',
                      fontWeight: 700, fontSize: 11,
                    }}
                  />
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Paper>

      {/* Filtros del histórico */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <TextField select size="small" label="Embarcación"
          value={filtros.embarcacion_id} onChange={setF('embarcacion_id')}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Todas</MenuItem>
          {embarcaciones.map(e => (
            <MenuItem key={e.id} value={e.id}>{e.nombre}</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Momento"
          value={filtros.momento} onChange={setF('momento')}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">Ambos</MenuItem>
          <MenuItem value="am">AM</MenuItem>
          <MenuItem value="pm">PM</MenuItem>
        </TextField>
        <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }}
          value={filtros.fecha_desde} onChange={setF('fecha_desde')} sx={{ width: 150 }} />
        <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }}
          value={filtros.fecha_hasta} onChange={setF('fecha_hasta')} sx={{ width: 150 }} />
      </Paper>

      {/* Tabla histórica */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Embarcación</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Momento</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Galones</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Nivel %</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Medido por</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Foto</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                <CircularProgress size={24} sx={{ color: '#0ea5e9' }} />
              </TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                Sin lecturas en el rango seleccionado.
              </TableCell></TableRow>
            )}
            {!loading && rows.map(l => {
              const m = MOMENTO[l.momento] || {};
              return (
                <TableRow key={l.id} hover>
                  <TableCell sx={{ fontSize: 12 }}>{fmtDate(l.fecha)}</TableCell>
                  <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{l.embarcacion_nombre}</TableCell>
                  <TableCell align="center">
                    <Chip label={m.label} size="small"
                      sx={{ bgcolor: m.bg, color: m.fg, fontWeight: 800, fontSize: 11, width: 44 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13, fontWeight: 600 }}>
                    {l.nivel_galones != null ? Number(l.nivel_galones).toLocaleString('es-CO', { maximumFractionDigits: 2 }) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, color: '#64748b' }}>
                    {l.nivel_pct != null ? `${l.nivel_pct}%` : '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: 11, color: '#64748b' }}>
                    {l.medido_por_nombre || '—'}
                  </TableCell>
                  <TableCell align="center">
                    {l.foto_medidor_url && (
                      <IconButton size="small" onClick={() => window.open(l.foto_medidor_url, '_blank')}>
                        <CameraAltIcon fontSize="small" sx={{ color: '#0ea5e9' }} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <NuevaLecturaDialog
        open={nuevaOpen}
        embarcaciones={embarcaciones}
        empresaId={empresaId}
        onClose={() => setNuevaOpen(false)}
        onSuccess={(msg) => {
          setNuevaOpen(false);
          setSnack({ open: true, msg, severity: 'success' });
          cargar();
        }}
        onError={(msg) => setSnack({ open: true, msg, severity: 'error' })}
      />

      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
